import type {
  ArcaEnvironment,
  ArcaSetupValidateResult,
  FiscalContext,
  FiscalProfile,
  FiscalProfileInput,
  TaxRegime,
} from '../domain/fiscalTypes';
import { isValidIsoDateString } from '../domain/fiscalTypes';
import { buildLibroIvaComprasCsv } from '../domain/libroIvaComprasCsv';
import type { LibroIvaVentaRow } from '../domain/libroIvaTxt';
import type { LibroIvaCompraRow } from '../domain/libroIvaComprasTxt';
import { consultaPadron } from './padronAdapter';
import { parseSupabaseInvokeError } from './parseSupabaseInvokeError';
import { invalidateFiscalContextCache } from './fiscalConfigRepo';
import { buildCsvFromRows } from '../../shared/csv-core/infra/buildCsv';
import { invokeEdgeOrPresent } from '../../shared/supabase/invokeEdgeOrPresent'
import { supabase } from '../../shared/supabase/supabaseClient';

export type SaveFiscalProfileOptions = {
  regimeChanged?: boolean;
};

const fiscalTextOrNull = (value?: string | null): string | null => {
  const trimmed = value?.trim() ?? '';
  return trimmed.length > 0 ? trimmed : null;
};

/** Solo persiste `YYYY-MM-DD`; cualquier otro formato va como null. */
const fiscalIsoDateOrNull = (value?: string | null): string | null =>
  isValidIsoDateString(value) ? value.trim() : null;

const postgrestErrorMessage = (
  error: { message?: string; details?: string; hint?: string } | null,
  fallback: string
): string => {
  if (!error) return fallback;
  return [error.message, error.details, error.hint].filter(Boolean).join(' | ') || fallback;
};

/** Guarda perfil fiscal org + PV opcional de sucursal. */
export const saveFiscalProfileRemote = async (
  branchId: string,
  profile: FiscalProfileInput,
  options?: SaveFiscalProfileOptions
): Promise<{ ok: boolean; organizationId: string }> => {
  const body: Record<string, unknown> = {
    branch_id: branchId,
    tax_regime: profile.taxRegime,
    emitter_cuit: profile.emitterCuit.replace(/\D/g, ''),
    business_name: profile.businessName.trim(),
    domicilio_comercial: fiscalTextOrNull(profile.domicilioComercial),
    iibb: fiscalTextOrNull(profile.iibb),
    inicio_actividades: fiscalIsoDateOrNull(profile.inicioActividades),
    monotributo_category: profile.monotributoCategory ?? null,
    environment: profile.environment,
    regime_changed: options?.regimeChanged === true,
  };
  if (profile.pointOfSale !== undefined) {
    body.point_of_sale = profile.pointOfSale;
  }

  const { data, error, presented } = await invokeEdgeOrPresent('fiscal-profile-upsert', { body }, {
    module: 'fiscal',
    op: 'fiscal.profile_upsert',
    fallbackMessage: 'Error al guardar perfil fiscal',
  });
  const res = (data ?? {}) as { ok?: boolean; organization_id?: string; error?: string };
  if (error || res?.error) {
    if (presented) {
      throw error instanceof Error
        ? error
        : new Error(res?.error ?? 'Error al guardar perfil fiscal');
    }
    const message = await parseSupabaseInvokeError(error, 'Error al guardar perfil fiscal', res?.error);
    throw new Error(message);
  }
  if (res?.ok !== true) throw new Error('No se pudo guardar el perfil fiscal');
  invalidateFiscalContextCache(branchId);
  return { ok: true, organizationId: res.organization_id ?? '' };
};

export type ValidateArcaSetupOptions = {
  certPem?: string;
  privateKeyPem?: string;
  emitterCuit?: string;
  pointOfSale?: number;
  taxRegime?: TaxRegime;
  environment?: ArcaEnvironment;
  validateBranchPv?: boolean;
};

/** Valida WSAA + último comprobante (Edge arca-setup-validate). */
export const validateArcaSetup = async (
  branchId: string,
  testCuit?: string,
  options?: ValidateArcaSetupOptions
): Promise<ArcaSetupValidateResult & { lastInvoiceNumber?: number }> => {
  const body: Record<string, unknown> = { branch_id: branchId };
  if (options?.certPem) body.cert_pem = options.certPem;
  if (options?.privateKeyPem) body.private_key_pem = options.privateKeyPem;
  if (options?.emitterCuit) body.emitter_cuit = options.emitterCuit.replace(/\D/g, '');
  if (options?.pointOfSale != null) body.point_of_sale = options.pointOfSale;
  if (options?.taxRegime) body.tax_regime = options.taxRegime;
  if (options?.environment) body.environment = options.environment;
  if (options?.validateBranchPv === true) body.validate_branch_pv = true;

  const { data, error, presented } = await invokeEdgeOrPresent('arca-setup-validate', { body }, {
    module: 'fiscal',
    op: 'fiscal.arca_setup_validate',
    fallbackMessage: 'Error al validar ARCA',
  });
  const res = (data ?? {}) as ArcaSetupValidateResult & {
    lastInvoiceNumber?: number;
    setup_status?: string;
    error?: string;
    pv_validated?: boolean;
  };

  if (error || !res?.ok) {
    const message = presented
      ? (res?.error ?? error?.message ?? 'Error al validar ARCA')
      : await parseSupabaseInvokeError(error, 'Error al validar ARCA', res?.error);
    return {
      ok: false,
      error: message,
      setupStatus: res?.setupStatus ?? (res?.setup_status as FiscalProfile['setupStatus']) ?? 'error',
    };
  }

  let razonSocial: string | undefined;
  let cuit = testCuit;
  let fromCache: boolean | undefined;
  if (testCuit) {
    try {
      const padron = await consultaPadron(testCuit.replace(/\D/g, ''), branchId);
      razonSocial = padron.razonSocial;
      cuit = padron.cuit;
      fromCache = padron.fromCache;
    } catch {
      // La validación WSAA puede OK aunque el padrón falle en sandbox.
    }
  }

  // BUG-1 fix: invalida cache tras validación exitosa (profile/branchFiscal cambian server-side).
  invalidateFiscalContextCache(branchId);

  return {
    ok: true,
    setupStatus: 'validated',
    lastInvoiceNumber: res.lastInvoiceNumber,
    pvValidated: res.pv_validated ?? options?.validateBranchPv === true,
    razonSocial,
    cuit,
    fromCache,
  };
};

export type ArcaCsrGenerateResult = {
  ok: boolean;
  csrPem?: string;
  fileName?: string;
  cuit?: string;
  environment?: ArcaEnvironment;
  reused?: boolean;
  error?: string;
};

/** Genera/descarga el CSR ARCA server-side (Edge arca-csr-generate). #1510 */
export const generateArcaCsr = async (
  branchId: string,
  options?: { environment?: ArcaEnvironment; regenerate?: boolean }
): Promise<ArcaCsrGenerateResult> => {
  const body: Record<string, unknown> = { branch_id: branchId };
  if (options?.environment) body.environment = options.environment;
  if (options?.regenerate === true) body.regenerate = true;

  const { data, error, presented } = await invokeEdgeOrPresent('arca-csr-generate', { body }, {
    module: 'fiscal',
    op: 'fiscal.arca_csr_generate',
    fallbackMessage: 'No se pudo generar el CSR',
  });
  const res = (data ?? {}) as {
    ok?: boolean;
    csr_pem?: string;
    file_name?: string;
    cuit?: string;
    environment?: ArcaEnvironment;
    reused?: boolean;
    error?: string;
  };

  if (error || res?.ok !== true || !res.csr_pem) {
    const message = presented
      ? (res?.error ?? error?.message ?? 'No se pudo generar el CSR')
      : await parseSupabaseInvokeError(error, 'No se pudo generar el CSR', res?.error);
    return { ok: false, error: message };
  }

  invalidateFiscalContextCache(branchId);
  return {
    ok: true,
    csrPem: res.csr_pem,
    fileName: res.file_name,
    cuit: res.cuit,
    environment: res.environment,
    reused: res.reused === true,
  };
};

/** Valida solo el PV ARCA de una sucursal (org ya validada). */
export const validateBranchPointOfSale = async (
  branchId: string,
  pointOfSale: number,
  options?: Omit<ValidateArcaSetupOptions, 'validateBranchPv' | 'pointOfSale'>
): Promise<ArcaSetupValidateResult & { lastInvoiceNumber?: number }> =>
  validateArcaSetup(branchId, undefined, {
    ...options,
    pointOfSale,
    validateBranchPv: true,
  });

/** Total fiscal YTD de la sucursal (ventas modo fiscal con CAE aprobado). */
export const fetchFiscalYtdTotal = async (branchId: string): Promise<number> => {
  const yearStart = `${new Date().getFullYear()}-01-01T00:00:00.000Z`;
  const { data, error } = await supabase
    .schema('pos')
    .from('sales')
    .select('total')
    .eq('branch_id', branchId)
    .eq('fiscal_mode', 'fiscal')
    .eq('fiscal_status', 'approved')
    .neq('status', 'voided')
    .gte('created_at', yearStart);
  if (error) {
    console.warn('[fiscal] fetchFiscalYtdTotal', error.message);
    return 0;
  }
  return (data ?? []).reduce((sum, row) => sum + Number((row as { total?: number }).total ?? 0), 0);
};

export type FiscalYtdTotals = {
  orgTotal: number;
  branchTotal: number;
};

/** Totales YTD org + sucursal en una sola consulta (evita GET duplicado a sales). */
export const fetchFiscalYtdTotals = async (
  branchIds: string[],
  currentBranchId: string
): Promise<FiscalYtdTotals> => {
  if (branchIds.length === 0) {
    return { orgTotal: 0, branchTotal: 0 };
  }
  const yearStart = `${new Date().getFullYear()}-01-01T00:00:00.000Z`;
  const { data, error } = await supabase
    .schema('pos')
    .from('sales')
    .select('total, branch_id')
    .in('branch_id', branchIds)
    .eq('fiscal_mode', 'fiscal')
    .eq('fiscal_status', 'approved')
    .neq('status', 'voided')
    .gte('created_at', yearStart);
  if (error) {
    console.warn('[fiscal] fetchFiscalYtdTotals', error.message);
    return { orgTotal: 0, branchTotal: 0 };
  }
  let orgTotal = 0;
  let branchTotal = 0;
  for (const row of data ?? []) {
    const total = Number((row as { total?: number }).total ?? 0);
    orgTotal += total;
    if ((row as { branch_id?: string }).branch_id === currentBranchId) {
      branchTotal += total;
    }
  }
  return { orgTotal, branchTotal };
};

/** Total fiscal YTD org-wide (todas las sucursales). */
export const fetchFiscalYtdTotalOrg = async (organizationId: string): Promise<number> => {
  const { data: branchRows, error: branchError } = await supabase
    .from('branches')
    .select('id')
    .eq('organization_id', organizationId);
  if (branchError) {
    console.warn('[fiscal] fetchFiscalYtdTotalOrg branches', branchError.message);
    return 0;
  }
  const branchIds = (branchRows ?? []).map((row) => (row as { id: string }).id);
  if (branchIds.length === 0) return 0;
  const { orgTotal } = await fetchFiscalYtdTotals(branchIds, branchIds[0]);
  return orgTotal;
};

/** Export CSV simplificado libro IVA ventas (YTD, sucursal). */
export const exportLibroIvaVentasCsv = async (branchId: string): Promise<string> => {
  const yearStart = `${new Date().getFullYear()}-01-01T00:00:00.000Z`;
  // invoices embebidas en la misma query: un `.in('sale_id', saleIds)` por
  // separado serializa N UUIDs en la URL y rompe con 414 a ~200 ventas (#1582).
  const { data: sales, error } = await supabase
    .schema('pos')
    .from('sales')
    .select(
      'id, total, created_at, fiscal_buyer_cuit, fiscal_buyer_name, fiscal_invoice_type, invoices(sale_id, cae, invoice_type, invoice_number, point_of_sale, net_amount, iva_amount, total_amount, issued_at, status)'
    )
    .eq('branch_id', branchId)
    .eq('fiscal_mode', 'fiscal')
    .eq('fiscal_status', 'approved')
    .neq('status', 'voided')
    .gte('created_at', yearStart)
    .order('created_at', { ascending: true });

  if (error) throw new Error(error.message || 'No se pudo leer ventas fiscales');

  const header = [
    'fecha',
    'tipo_cbte',
    'pto_vta',
    'nro_cbte',
    'cuit_comprador',
    'razon_social',
    'neto',
    'iva',
    'total',
    'cae',
  ];

  const rows = (sales ?? []).map((row) => {
    const s = row as Record<string, unknown>;
    const embedded = Array.isArray(s.invoices) ? (s.invoices as Record<string, unknown>[]) : [];
    const inv =
      embedded.find((i) => i.status === 'approved') ?? embedded[embedded.length - 1] ?? {};
    return [
      String(s.created_at ?? '').slice(0, 10),
      String(inv.invoice_type ?? s.fiscal_invoice_type ?? ''),
      String(inv.point_of_sale ?? ''),
      String(inv.invoice_number ?? ''),
      String(s.fiscal_buyer_cuit ?? ''),
      String(s.fiscal_buyer_name ?? ''),
      String(inv.net_amount ?? ''),
      String(inv.iva_amount ?? ''),
      String(inv.total_amount ?? s.total ?? ''),
      String(inv.cae ?? ''),
    ];
  });

  return buildCsvFromRows(header, rows, ';');
};

/** Datos del emisor resueltos server-side (perfil fiscal de la org). */
export type FiscalEmissionEmitter = {
  businessName?: string | null;
  cuit?: string | null;
  taxRegime?: string | null;
  domicilioComercial?: string | null;
  iibb?: string | null;
  inicioActividades?: string | null;
  environment?: string | null;
};

export type FiscalInvoiceEmitResult = {
  cae?: string;
  caeExpiration?: string | null;
  status?: string;
  issuedAt?: string;
  qrUrl?: string;
  invoiceType?: number;
  pointOfSale?: number;
  invoiceNumber?: number;
  netAmount?: number;
  ivaAmount?: number;
  totalAmount?: number;
  buyerName?: string | null;
  buyerCuit?: string | null;
  buyerTaxCondition?: number | null;
  emitter?: FiscalEmissionEmitter | null;
  /** La venta ya tenía comprobante aprobado — la EF devolvió el existente (idempotente). */
  alreadyEmitted?: boolean;
  sandbox?: boolean;
  error?: string;
};

/** Reintenta emisión fiscal de una venta (Edge arca-invoice). */
export const retryFiscalInvoiceEmit = async (
  saleId: string,
  branchId?: string | null
): Promise<FiscalInvoiceEmitResult> => {
  // BUG-9 fix: el campo se llamaba "customer" pero la edge function lo ignora
  // (resuelve branchId server-side desde la sale). Se elimina el campo muerto.
  const { data, error, presented } = await invokeEdgeOrPresent('arca-invoice', {
    body: { saleId },
  }, {
    module: 'fiscal',
    op: 'fiscal.arca_invoice_retry',
    fallbackMessage: 'No se pudo reintentar la factura',
  });
  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'No se pudo reintentar la factura');
    }
    throw new Error(error.message || 'No se pudo reintentar la factura');
  }
  const body = (data ?? {}) as FiscalInvoiceEmitResult;
  if (body?.error) throw new Error(body.error);

  if (body.status === 'approved' || body.cae?.trim()) {
    // ADR-017: local SQLite patch removed — remote invoice status is authoritative.
  }

  return body;
};

export type FiscalInvoiceConsultResult = {
  ok?: boolean;
  error?: string;
  environment?: string;
  afip?: {
    cae?: string;
    cbteFch?: string;
    impTotal?: number;
    ptoVta?: number;
    cbteTipo?: number;
    docTipo?: number;
    docNro?: number;
  };
  stored?: {
    cae?: string | null;
    totalAmount?: number | null;
  } | null;
  matches?: {
    cae?: boolean;
    importe?: boolean | null;
  } | null;
};

export const consultFiscalInvoiceInAfip = async (
  saleId: string,
  branchId?: string | null
): Promise<FiscalInvoiceConsultResult> => {
  const { data, error, presented } = await invokeEdgeOrPresent('arca-invoice-consult', {
    body: { saleId, branchId: branchId ?? null },
  }, {
    module: 'fiscal',
    op: 'fiscal.arca_invoice_consult',
    fallbackMessage: 'No se pudo constatar en ARCA',
  });
  if (error) {
    if (presented) {
      throw error instanceof Error ? error : new Error(error.message || 'No se pudo constatar en ARCA');
    }
    throw new Error(error.message || 'No se pudo constatar en ARCA');
  }
  const body = (data ?? {}) as FiscalInvoiceConsultResult;
  if (body?.error) throw new Error(body.error);
  return body;
};

export const retryFiscalCreditNoteEmit = async (saleId: string): Promise<FiscalInvoiceEmitResult> => {
  const { data, error, presented } = await invokeEdgeOrPresent('arca-credit-note', { body: { saleId } }, {
    module: 'fiscal',
    op: 'fiscal.arca_credit_note',
    fallbackMessage: 'No se pudo reintentar la nota de crédito',
  });
  if (error) {
    if (presented) {
      throw error instanceof Error
        ? error
        : new Error(error.message || 'No se pudo reintentar la nota de crédito');
    }
    throw new Error(error.message || 'No se pudo reintentar la nota de crédito');
  }
  const body = (data ?? {}) as FiscalInvoiceEmitResult & { status?: string; error?: string };
  if (body?.error && body.status !== 'pending_retry') throw new Error(body.error);
  return body;
};

export type CreditNoteListRow = {
  id: string;
  saleId: string;
  cae: string | null;
  invoiceType: string | null;
  pointOfSale: number | null;
  invoiceNumber: number | null;
  issuedAt: string | null;
  status: string | null;
  totalAmount: number | null;
};

/** Notas de crédito emitidas (invoices con related_invoice_id) para la sucursal. */
export const fetchCreditNotesForBranch = async (
  branchId: string,
  limit = 25
): Promise<CreditNoteListRow[]> => {
  const { data: sales, error: salesError } = await supabase
    .schema('pos')
    .from('sales')
    .select('id')
    .eq('branch_id', branchId)
    .eq('status', 'voided')
    .order('voided_at', { ascending: false })
    .limit(100);

  if (salesError) {
    console.warn('[fiscal] fetchCreditNotesForBranch sales', salesError.message);
    return [];
  }

  const saleIds = (sales ?? []).map((row) => (row as { id: string }).id);
  if (saleIds.length === 0) return [];

  const { data: invoices, error } = await supabase
    .schema('pos')
    .from('invoices')
    .select(
      'id, sale_id, cae, invoice_type, point_of_sale, invoice_number, issued_at, status, total_amount'
    )
    .in('sale_id', saleIds)
    .not('related_invoice_id', 'is', null)
    .in('invoice_type', [3, 8, 13])
    .order('issued_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.warn('[fiscal] fetchCreditNotesForBranch invoices', error.message);
    return [];
  }

  return (invoices ?? []).map((row) => {
    const inv = row as Record<string, unknown>;
    return {
      id: String(inv.id ?? ''),
      saleId: String(inv.sale_id ?? ''),
      cae: typeof inv.cae === 'string' ? inv.cae : null,
      invoiceType: typeof inv.invoice_type === 'string' ? inv.invoice_type : null,
      pointOfSale: typeof inv.point_of_sale === 'number' ? inv.point_of_sale : null,
      invoiceNumber: typeof inv.invoice_number === 'number' ? inv.invoice_number : null,
      issuedAt: typeof inv.issued_at === 'string' ? inv.issued_at : null,
      status: typeof inv.status === 'string' ? inv.status : null,
      totalAmount: typeof inv.total_amount === 'number' ? inv.total_amount : null,
    };
  });
};

/** Comprobante emitido (factura o NC) — datos completos para imprimir. */
export type FiscalInvoiceDetailRow = {
  id: string;
  cae: string | null;
  caeExpiration: string | null;
  invoiceType: number | null;
  pointOfSale: number | null;
  invoiceNumber: number | null;
  issuedAt: string | null;
  status: string | null;
  totalAmount: number | null;
  netAmount: number | null;
  ivaAmount: number | null;
  buyerName: string | null;
  buyerCuit: string | null;
  buyerTaxCondition: number | null;
  qrPayload: string | null;
};

const INVOICE_DETAIL_SELECT =
  'id, cae, cae_expiration, invoice_type, point_of_sale, invoice_number, issued_at, status, total_amount, net_amount, iva_amount, buyer_name, buyer_cuit, buyer_tax_condition, qr_payload';

const mapInvoiceDetailRow = (row: unknown): FiscalInvoiceDetailRow => {
  const inv = row as Record<string, unknown>;
  return {
    id: String(inv.id ?? ''),
    cae: typeof inv.cae === 'string' ? inv.cae : null,
    caeExpiration: typeof inv.cae_expiration === 'string' ? inv.cae_expiration : null,
    invoiceType: typeof inv.invoice_type === 'number' ? inv.invoice_type : null,
    pointOfSale: typeof inv.point_of_sale === 'number' ? inv.point_of_sale : null,
    invoiceNumber: typeof inv.invoice_number === 'number' ? inv.invoice_number : null,
    issuedAt: typeof inv.issued_at === 'string' ? inv.issued_at : null,
    status: typeof inv.status === 'string' ? inv.status : null,
    totalAmount: typeof inv.total_amount === 'number' ? inv.total_amount : null,
    netAmount: typeof inv.net_amount === 'number' ? inv.net_amount : null,
    ivaAmount: typeof inv.iva_amount === 'number' ? inv.iva_amount : null,
    buyerName: typeof inv.buyer_name === 'string' ? inv.buyer_name : null,
    buyerCuit: typeof inv.buyer_cuit === 'string' ? inv.buyer_cuit : null,
    buyerTaxCondition: typeof inv.buyer_tax_condition === 'number' ? inv.buyer_tax_condition : null,
    qrPayload: typeof inv.qr_payload === 'string' ? inv.qr_payload : null,
  };
};

/** Factura principal de la venta (related_invoice_id IS NULL). Null si no hay. */
export const fetchFiscalInvoiceForSale = async (
  saleId: string
): Promise<FiscalInvoiceDetailRow | null> => {
  const { data, error } = await supabase
    .schema('pos')
    .from('invoices')
    .select(INVOICE_DETAIL_SELECT)
    .eq('sale_id', saleId)
    .is('related_invoice_id', null)
    .order('issued_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[fiscal] fetchFiscalInvoiceForSale', error.message);
    return null;
  }
  return data ? mapInvoiceDetailRow(data) : null;
};

/** NC aprobada de una venta (invoice con related_invoice_id). Null si no hay. */
export const fetchCreditNoteForSale = async (
  saleId: string
): Promise<FiscalInvoiceDetailRow | null> => {
  const { data, error } = await supabase
    .schema('pos')
    .from('invoices')
    .select(INVOICE_DETAIL_SELECT)
    .eq('sale_id', saleId)
    .not('related_invoice_id', 'is', null)
    .in('invoice_type', [3, 8, 13])
    .order('issued_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (error) {
    console.warn('[fiscal] fetchCreditNoteForSale', error.message);
    return null;
  }
  return data ? mapInvoiceDetailRow(data) : null;
};

/** Notas de débito emitidas/pendientes de una venta (tipos 2/7/12). */
export const fetchDebitNotesForSale = async (
  saleId: string
): Promise<FiscalInvoiceDetailRow[]> => {
  const { data, error } = await supabase
    .schema('pos')
    .from('invoices')
    .select(INVOICE_DETAIL_SELECT)
    .eq('sale_id', saleId)
    .not('related_invoice_id', 'is', null)
    .in('invoice_type', [2, 7, 12])
    .order('issued_at', { ascending: false })
    .limit(20);

  if (error) {
    console.warn('[fiscal] fetchDebitNotesForSale', error.message);
    return [];
  }
  return (data ?? []).map(mapInvoiceDetailRow);
};

/** Emite nota de débito sobre la factura aprobada de la venta. */
export const emitFiscalDebitNote = async (
  saleId: string,
  amount: number,
  reason?: string | null,
  debitNoteId?: string | null
): Promise<{ debitNoteInvoiceId?: string; cae?: string; status?: string; error?: string; nextRetryAt?: string }> => {
  const { data, error } = await supabase.functions.invoke('arca-debit-note', {
    body: { saleId, amount, reason: reason ?? null, debitNoteId: debitNoteId ?? null },
  });
  const payload = (data ?? {}) as Record<string, unknown>;
  if (error) {
    return {
      error: String(payload.error ?? error.message),
      status: typeof payload.status === 'string' ? payload.status : 'error',
      debitNoteInvoiceId:
        typeof payload.debitNoteInvoiceId === 'string' ? payload.debitNoteInvoiceId : undefined,
      nextRetryAt: typeof payload.nextRetryAt === 'string' ? payload.nextRetryAt : undefined,
    };
  }
  return {
    debitNoteInvoiceId:
      typeof payload.debitNoteInvoiceId === 'string' ? payload.debitNoteInvoiceId : undefined,
    cae: typeof payload.cae === 'string' ? payload.cae : undefined,
    status: typeof payload.status === 'string' ? payload.status : undefined,
  };
};

/**
 * Comprobantes fiscales aprobados del período para Libro IVA Digital:
 * facturas + NC + ND (todos los documentos con related_invoice_id
 * entran; se discriminan por invoice_type en el export).
 */
export const fetchLibroIvaVentasRows = async (
  branchId: string,
  fromIso: string,
  toIso: string
): Promise<LibroIvaVentaRow[]> => {
  // Desde `sales` + `invoices!inner` (mismo criterio branch/fiscal_mode que CSV YTD, #1582).
  // Filtro de período en `issued_at` del comprobante; evita embed invertido que en web
  // devolvía PostgrestError opaco ("Bad Request") con RLS + filtros anidados.
  const { data: sales, error } = await supabase
    .schema('pos')
    .from('sales')
    .select(
      `invoices!inner(
        issued_at, invoice_type, point_of_sale, invoice_number,
        buyer_cuit, buyer_name, total_amount, net_amount, iva_amount, iva_breakdown, status
      )`
    )
    .eq('branch_id', branchId)
    .eq('fiscal_mode', 'fiscal')
    .eq('invoices.status', 'approved')
    .gte('invoices.issued_at', fromIso)
    .lt('invoices.issued_at', toIso);

  if (error) throw new Error(postgrestErrorMessage(error, 'No se pudo leer comprobantes'));

  const rows: LibroIvaVentaRow[] = [];
  for (const sale of sales ?? []) {
    const embedded = (sale as { invoices: unknown }).invoices;
    const invoices = Array.isArray(embedded) ? embedded : embedded ? [embedded] : [];
    for (const inv of invoices) {
      const row = inv as Record<string, unknown>;
      rows.push({
        issuedAt: String(row.issued_at ?? ''),
        invoiceType: Number(row.invoice_type ?? 0),
        pointOfSale: Number(row.point_of_sale ?? 0),
        invoiceNumber: Number(row.invoice_number ?? 0),
        buyerCuit: typeof row.buyer_cuit === 'string' ? row.buyer_cuit : null,
        buyerName: typeof row.buyer_name === 'string' ? row.buyer_name : null,
        totalAmount: Number(row.total_amount ?? 0),
        netAmount: Number(row.net_amount ?? 0),
        ivaAmount: Number(row.iva_amount ?? 0),
        ivaBreakdown: (row.iva_breakdown as LibroIvaVentaRow['ivaBreakdown']) ?? null,
      });
    }
  }

  rows.sort(
    (a, b) =>
      a.issuedAt.localeCompare(b.issuedAt) ||
      a.invoiceType - b.invoiceType ||
      a.pointOfSale - b.pointOfSale ||
      a.invoiceNumber - b.invoiceNumber
  );
  return rows;
};

/**
 * Libro IVA COMPRAS — comprobantes fiscales recibidos del período.
 * Lee `pos.expenses` directo (policy authenticated): solo gastos activos con
 * bloque fiscal cargado (`invoice_doc_type not null`). El gasto informal no
 * entra al libro.
 */
export const fetchLibroIvaComprasRows = async (
  branchId: string,
  fromDate: string,
  toDate: string
): Promise<LibroIvaCompraRow[]> => {
  const { data, error } = await supabase
    .schema('pos')
    .from('expenses')
    .select(
      'expense_date, amount, invoice_doc_type, invoice_pv, invoice_number, supplier_tax_id, supplier_name, invoice_iva_breakdown, invoice_perceptions'
    )
    .eq('branch_id', branchId)
    .eq('status', 'active')
    .not('invoice_doc_type', 'is', null)
    .gte('expense_date', fromDate)
    .lt('expense_date', toDate)
    .order('expense_date', { ascending: true })
    .order('invoice_doc_type', { ascending: true })
    .order('invoice_number', { ascending: true });

  if (error) {
    throw new Error(postgrestErrorMessage(error, 'No se pudieron leer los comprobantes de compras'));
  }

  return (data ?? []).map((row) => {
    const e = row as Record<string, unknown>;
    return {
      expenseDate: String(e.expense_date ?? ''),
      amount: Number(e.amount ?? 0),
      invoiceDocType: Number(e.invoice_doc_type ?? 0),
      invoicePv: Number(e.invoice_pv ?? 0),
      invoiceNumber: Number(e.invoice_number ?? 0),
      supplierTaxId: typeof e.supplier_tax_id === 'string' ? e.supplier_tax_id : null,
      supplierName: typeof e.supplier_name === 'string' ? e.supplier_name : null,
      breakdown: (e.invoice_iva_breakdown as LibroIvaCompraRow['breakdown']) ?? null,
      perceptions: (e.invoice_perceptions as LibroIvaCompraRow['perceptions']) ?? null,
    };
  });
};

/** CSV legible de compras fiscales del período (planilla / Excel). */
export const exportLibroIvaComprasCsv = async (
  branchId: string,
  fromDate: string,
  toDate: string
): Promise<string> => {
  const rows = await fetchLibroIvaComprasRows(branchId, fromDate, toDate);
  return buildLibroIvaComprasCsv(rows);
};

export type { FiscalContext };
